import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'
import FlagIcon from '@material-ui/icons/Flag'

import { Container, MessageDrawer } from '../../../components'
import {
    FlagInventoryInspectionData,
    useFlagInventoryInspections,
} from '../../../hooks/useFlagInventoryInspections'
import {
    useAptConfig,
    useRootInfrastructure,
    _useFinderSelection,
    useMessageDrawer,
    useService,
    useSchedule,
    useUser,
    useCompany,
} from '../../../hooks'
import {
    createWorkorder,
    FinderLocationSelection,
    FinderSelectionMode,
} from '../../../store'
import { InventoryInspectionWorkorderModal } from '../InventoryInspectionWorkorderModal'
import { InventoryInspectionAnalytic } from '../InventoryInspectionAnalyticsBlanket'
import { useAppDispatch } from '../../../hooks'
import {
    FlagInspectionFilter,
    FlagInspectionFilterDrawer,
    defaultFlagInspectionFilter,
    AttachmentFilterValue,
    WorkorderFilterValue,
    HandledFilterValue,
} from './FlagInspectionFilterDrawer'
import { FlagInspectionFilterChips } from './FlagInspectionFilterChips'
import {
    FlaggedInspectionRow,
    getAreaDisplayName,
} from './FlaggedInspectionRow'

const ITEMS_PER_PAGE = 25

interface Props {
    inspectionId?: number
}

export const FlagInventoryInspectionsHome = ({ inspectionId }: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const {
        flagInventoryInspections,
        getFlagInventoryInspections,
        updateInvInsState,
    } = useFlagInventoryInspections()

    const { inventoryConfigList } = useAptConfig({ inventoryConfigList: true })
    const { tree } = useRootInfrastructure(true)
    const finderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'inspection',
    })

    const { setChannel } = useMessageDrawer()
    const { serviceList, getServiceList } = useService()
    const { scheduleList } = useSchedule()
    const { userList } = useUser()
    const { vendorList } = useCompany({ getVendorList: true, cleanUp: true })

    const [loading, setLoading] = useState(true)
    const [searchText, setSearchText] = useState('')
    const [page, setPage] = useState(1)
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [filter, setFilter] = useState<FlagInspectionFilter>(
        defaultFlagInspectionFilter,
    )
    const [appliedFilter, setAppliedFilter] = useState<FlagInspectionFilter>(
        defaultFlagInspectionFilter,
    )
    const [openMessageItemId, setOpenMessageItemId] = useState(-1)
    const [workorderModalOpen, setWorkorderModalOpen] = useState(false)
    const [workorderLoading, setWorkorderLoading] = useState(false)
    const [
        selectedItemForWorkorder,
        setSelectedItemForWorkorder,
    ] = useState<FlagInventoryInspectionData | null>(null)

    const onOpenMessageDrawer = (channelId: number, itemId: number) => {
        setChannel(channelId)
        setOpenMessageItemId(itemId)
    }

    const onCreateWorkorder = (item: FlagInventoryInspectionData) => {
        setSelectedItemForWorkorder(item)
        setWorkorderModalOpen(true)
    }

    // Convert FlagInventoryInspectionData to InventoryInspectionAnalytic format
    const getInventoryInspectionAnalytics = (): InventoryInspectionAnalytic[] => {
        if (!selectedItemForWorkorder) return []

        const item = selectedItemForWorkorder
        return [
            {
                id: item.id,
                ins_name: item.area_inspection?.inspection?.name ?? '',
                channel: item.channel,
                ins_start_date: '',
                ins_end_date: '',
                folder_name:
                    item.area_inspection?.area?.unit?.folder_name ?? '',
                path: item.area_inspection?.area?.unit?.folder_path ?? '',
                unit_id: item.area_inspection?.area?.unit?.id ?? 0,
                unit_name: item.area_inspection?.area?.unit?.name ?? '',
                area_id: item.area_inspection?.area?.id ?? 0,
                area_label: item.area_inspection?.area?.area_label ?? '',
                area_name: item.area_inspection?.area?.area_config?.name ?? '',
                inv_name: item.inventory_config?.name ?? '',
                status: item.status?.name ?? '',
                status_color: item.status?.color ?? '',
                inv_ins_id: item.id,
                unread_count: 0,
                channel_message_count: item.area_inspection?.message_count ?? 0,
                wo_count: item.workorder_ids?.length ?? 0,
            },
        ]
    }

    useEffect(() => {
        setLoading(true)
        getFlagInventoryInspections(inspectionId).finally(() =>
            setLoading(false),
        )
        getServiceList({ getServiceList: true })
    }, [])

    const handleApplyFilter = (newFilter: FlagInspectionFilter) => {
        setAppliedFilter({ ...newFilter, modified: false })
        setFilter({ ...newFilter, modified: false })
        setFilterDrawerOpen(false)
        setPage(1)
    }

    // Filter data based on search text and applied filters
    const filteredData = useMemo(() => {
        let data = flagInventoryInspections

        // Apply inventory config filter
        if (appliedFilter.inventoryConfigId !== -1) {
            data = data.filter(
                (item) =>
                    item.inventory_config?.id ===
                    appliedFilter.inventoryConfigId,
            )
        }

        // Apply attachments filter
        if (
            appliedFilter.attachmentFilter ===
            AttachmentFilterValue.HAS_ATTACHMENTS
        ) {
            data = data.filter(
                (item) => item.attachments && item.attachments.length > 0,
            )
        } else if (
            appliedFilter.attachmentFilter ===
            AttachmentFilterValue.NO_ATTACHMENTS
        ) {
            data = data.filter(
                (item) => !item.attachments || item.attachments.length === 0,
            )
        }

        // Apply workorders filter
        if (
            appliedFilter.workorderFilter ===
            WorkorderFilterValue.HAS_WORKORDERS
        ) {
            data = data.filter(
                (item) => item.workorder_ids && item.workorder_ids.length > 0,
            )
        } else if (
            appliedFilter.workorderFilter === WorkorderFilterValue.NO_WORKORDERS
        ) {
            data = data.filter(
                (item) =>
                    !item.workorder_ids || item.workorder_ids.length === 0,
            )
        }

        // Apply handled filter
        if (appliedFilter.handledFilter === HandledFilterValue.HANDLED) {
            data = data.filter((item) => item.handled === true)
        } else if (
            appliedFilter.handledFilter === HandledFilterValue.NOT_HANDLED
        ) {
            data = data.filter((item) => item.handled !== true)
        }

        // Apply location filter (units) - with Recursive mode, folder selection auto-includes units
        if (finderSelection.selection.unit.length > 0) {
            data = data.filter((item) => {
                const itemUnitId = item.area_inspection?.area?.unit?.id ?? -1
                return finderSelection.selection.unit[itemUnitId] !== undefined
            })
        }

        // Apply search text
        if (searchText.trim()) {
            const lowerSearch = searchText.toLowerCase()
            data = data.filter((item) => {
                const areaName = getAreaDisplayName(item)
                const unitName = item.area_inspection?.area?.unit?.name ?? ''
                const inventoryName = item.inventory_config?.name ?? ''
                const statusName = item.status?.name ?? ''
                const inspectionName =
                    item.area_inspection?.inspection?.name ?? ''

                return (
                    areaName.toLowerCase().includes(lowerSearch) ||
                    unitName.toLowerCase().includes(lowerSearch) ||
                    inventoryName.toLowerCase().includes(lowerSearch) ||
                    statusName.toLowerCase().includes(lowerSearch) ||
                    inspectionName.toLowerCase().includes(lowerSearch)
                )
            })
        }

        return data
    }, [
        flagInventoryInspections,
        searchText,
        appliedFilter,
        finderSelection.selection,
    ])

    // Paginated data
    const paginatedData = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE
        return filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE)
    }, [filteredData, page])

    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE)

    // Reset to page 1 when search changes
    useEffect(() => {
        setPage(1)
    }, [searchText])

    const handlePageChange = (
        _event: React.ChangeEvent<unknown>,
        value: number,
    ) => {
        setPage(value)
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                padding: theme.spacing(3),
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Container
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(3),
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <FlagIcon
                        style={{
                            color: theme.palette.error.main,
                            fontSize: 28,
                        }}
                    />
                    <span
                        style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Flagged Inventory Inspections
                    </span>
                    {!loading && (
                        <span
                            style={{
                                fontSize: '0.875rem',
                                color: theme.palette.text.secondary,
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            ({filteredData.length} items)
                        </span>
                    )}
                </Container>

                <div style={{ flex: 1 }} />

                {/* Search */}
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Filter Button */}
                <IconButton
                    size="small"
                    onClick={() => setFilterDrawerOpen(true)}
                    style={{
                        marginLeft: theme.spacing(1),
                    }}
                >
                    <FilterListIcon />
                </IconButton>
            </Container>

            {/* Filter Chips */}
            <FlagInspectionFilterChips
                filter={appliedFilter}
                finderSelection={finderSelection}
                inventoryConfigList={inventoryConfigList ?? []}
                setFilter={(newFilter) => {
                    setAppliedFilter(newFilter)
                    setFilter(newFilter)
                }}
            />

            {/* Content */}
            {loading ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress size={48} />
                </Container>
            ) : filteredData.length === 0 ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        color: theme.palette.text.secondary,
                    }}
                >
                    <FlagIcon style={{ fontSize: 64, opacity: 0.3 }} />
                    <span
                        style={{
                            marginTop: theme.spacing(2),
                            fontSize: '1.1rem',
                        }}
                    >
                        {searchText
                            ? 'No results found'
                            : 'No flagged inventory inspections'}
                    </span>
                </Container>
            ) : (
                <>
                    {/* Cards List */}
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            overflow: 'auto',
                            gap: theme.spacing(1.5),
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        {paginatedData.map((item) => (
                            <FlaggedInspectionRow
                                key={item.id}
                                item={item}
                                messageDrawerOpen={
                                    openMessageItemId === item.id
                                }
                                onOpenMessages={(channelId) => {
                                    onOpenMessageDrawer(channelId, item.id)
                                }}
                                updateInvInsState={updateInvInsState}
                                onCreateWorkorder={onCreateWorkorder}
                            />
                        ))}
                    </Container>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: theme.spacing(2),
                                gap: theme.spacing(2),
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                shape="rounded"
                            />
                            <span
                                style={{
                                    fontSize: '0.875rem',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Showing {(page - 1) * ITEMS_PER_PAGE + 1} -{' '}
                                {Math.min(
                                    page * ITEMS_PER_PAGE,
                                    filteredData.length,
                                )}{' '}
                                of {filteredData.length}
                            </span>
                        </Container>
                    )}
                </>
            )}

            {/* Filter Drawer */}
            <FlagInspectionFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                tree={tree.root}
                selection={finderSelection}
                filterState={filter}
                setFilterState={setFilter}
                onApplyFilter={handleApplyFilter}
                inventoryConfigList={inventoryConfigList ?? []}
            />

            {/* Message Drawer */}
            <MessageDrawer
                open={openMessageItemId > 0}
                onClose={() => setOpenMessageItemId(-1)}
                onCreateMessage={() => {
                    setOpenMessageItemId(-1)
                }}
            />

            {/* Workorder Modal */}
            <InventoryInspectionWorkorderModal
                open={workorderModalOpen}
                handleClose={() => {
                    setWorkorderModalOpen(false)
                    setSelectedItemForWorkorder(null)
                }}
                serviceList={serviceList ?? []}
                vendorList={vendorList}
                userList={userList ?? []}
                scheduleList={scheduleList ?? []}
                inventoryInspections={getInventoryInspectionAnalytics()}
                loading={workorderLoading}
                initialTitle={
                    selectedItemForWorkorder?.inventory_config?.name ?? ''
                }
                onClickCreate={(request) => {
                    setWorkorderLoading(true)
                    dispatch(createWorkorder(request))
                        .then((res: any) => {
                            console.log(res)
                            toast.success('Workorder created successfully')
                            // Update the item's workorder_ids with the new workorder ID
                            if (
                                selectedItemForWorkorder &&
                                res?.data?.workorders
                            ) {
                                updateInvInsState({
                                    ...selectedItemForWorkorder,
                                    workorder_ids: [
                                        ...(selectedItemForWorkorder.workorder_ids ??
                                            []),
                                        res.data.workorders[0].id,
                                    ],
                                })
                            }
                            setWorkorderModalOpen(false)
                            setSelectedItemForWorkorder(null)
                        })
                        .catch((e) => {
                            toast.error(
                                e.response?.data?.message ??
                                    'Failed to create workorder',
                            )
                        })
                        .finally(() => {
                            setWorkorderLoading(false)
                        })
                }}
            />
        </Container>
    )
}
